import React, { useState, useCallback, useEffect } from 'react';
import { costService } from './services/costService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRefresh,
  faEye,
  faExclamationTriangle,
  faFilter,
  faDownload,
  faChartBar,
  faDatabase,
  faFileAlt,
  faFileInvoice,
  faChartLine,
  faBalanceScale
} from '@fortawesome/free-solid-svg-icons';
import { useCostData } from './hooks/useCostData';
import { CostFilters } from './types';
import {
  CostSummary,
  CloudBreakdownChart,
  AccountCostTable
} from './components';
import { VisualizationTab } from './components/VisualizationTab';
import { CustomDataTab, CustomCostEntry, CSVImportResult } from './components/CustomDataTab';
import { ReportTab } from './components/ReportTab';
import { BillsTab } from './components/BillsTab';
import { MonthlyTrendsTab } from './components/MonthlyTrendsTab';
import { ReconciliationTab } from './components/ReconciliationTab';
import { UnitEconomicsTab } from './components/UnitEconomicsTab';

export const CostAnalysisTab: React.FC = () => {
  console.log('CostAnalysisTab: Component rendered at', new Date().toISOString());
  
  const { 
    data, 
    loading, 
    error, 
    refetch, 
    lastUpdated,
    applyFilters,
    clearFilters,
  } = useCostData({
    autoRefresh: true,
    refreshInterval: 300000, // 5 minutes
    useMockData: false // Use real API data
  });

  // State for modals and UI
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<CostFilters>({});
  const [activeTab, setActiveTab] = useState<'summary' | 'visualization' | 'custom-data' | 'report' | 'bills' | 'monthly-trends' | 'reconciliation' | 'unit-economics'>('summary');
  const [includeCustomData, setIncludeCustomData] = useState(true);
  const [csvImportResults, setCsvImportResults] = useState<CSVImportResult[]>([]);
  
  // Custom data state — backed by DB via /api/cost/custom-data
  const [customEntries, setCustomEntries] = useState<CustomCostEntry[]>([]);
  const [customLoading, setCustomLoading] = useState(false);
  const [customError, setCustomError] = useState<string | null>(null);

  const loadCustomEntries = useCallback(async () => {
    setCustomLoading(true);
    setCustomError(null);
    try {
      const entries = await costService.getCustomEntries();
      setCustomEntries(entries as CustomCostEntry[]);
    } catch (err: any) {
      console.error('Failed to load custom entries:', err);
      setCustomError(err?.message || 'Failed to load custom entries');
    } finally {
      setCustomLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomEntries();
  }, [loadCustomEntries]);

  console.log('CostAnalysisTab: Hook state', { 
    hasData: !!data, 
    loading, 
    hasError: !!error, 
    lastUpdated,
    dataPreview: data ? {
      totalSpend: data.summary?.totalSpend,
      awsSpend: data.clouds?.aws?.totalSpend,
      gcpSpend: data.clouds?.gcp?.totalSpend,
      githubSpend: data.clouds?.github?.totalSpend
    } : null
  });

  const handleRefresh = async () => {
    console.log('Manual refresh triggered');
    await refetch();
  };

  const handleApplyFilters = useCallback((newFilters: CostFilters) => {
    setFilters(newFilters);
    applyFilters(newFilters);
    console.log('Filters applied:', newFilters);
  }, [applyFilters]);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    clearFilters();
    console.log('Filters cleared');
  }, [clearFilters]);

  // Custom data handlers — persist to DB via API
  const handleAddCustomEntry = useCallback(async (entryData: Omit<CustomCostEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const created = await costService.createCustomEntry({
        vendorName: entryData.vendorName,
        category: entryData.costType,
        amount: entryData.amount,
        currency: entryData.currency,
        month: entryData.month,
        dateRange: entryData.dateRange,
        notes: entryData.notes,
        source: 'manual'
      });
      setCustomEntries(prev => [...prev, created as CustomCostEntry]);
    } catch (err: any) {
      console.error('Failed to add custom entry:', err);
      setCustomError(err?.message || 'Failed to add entry');
    }
  }, []);

  const handleUpdateCustomEntry = useCallback(async (id: string, entryData: Partial<CustomCostEntry>) => {
    try {
      const updated = await costService.updateCustomEntry(id, {
        vendorName: entryData.vendorName,
        category: entryData.costType,
        amount: entryData.amount,
        currency: entryData.currency,
        month: entryData.month,
        dateRange: entryData.dateRange,
        notes: entryData.notes
      });
      setCustomEntries(prev => prev.map(e => e.id === id ? (updated as CustomCostEntry) : e));
    } catch (err: any) {
      console.error('Failed to update custom entry:', err);
      setCustomError(err?.message || 'Failed to update entry');
    }
  }, []);

  const handleDeleteCustomEntry = useCallback(async (id: string) => {
    try {
      await costService.deleteCustomEntry(id);
      setCustomEntries(prev => prev.filter(e => e.id !== id));
    } catch (err: any) {
      console.error('Failed to delete custom entry:', err);
      setCustomError(err?.message || 'Failed to delete entry');
    }
  }, []);

  // CSV Import handler — bulk insert via API then reload
  const handleCSVImport = useCallback(async (result: CSVImportResult) => {
    try {
      const payload = result.entries.map(e => ({
        vendorName: e.vendorName,
        category: e.costType,
        amount: e.amount,
        currency: e.currency,
        month: e.month,
        dateRange: e.dateRange,
        notes: e.notes
      }));
      await costService.bulkImportCustomEntries(payload);
      await loadCustomEntries();
      setCsvImportResults(prev => [...prev, result]);
    } catch (err: any) {
      console.error('CSV import failed:', err);
      setCustomError(err?.message || 'CSV import failed');
    }
  }, [loadCustomEntries]);

  // Calculate total including custom data
  const totalCustomCosts = customEntries.reduce((sum, entry) => sum + entry.amount, 0);
  const effectiveCustomCosts = includeCustomData ? totalCustomCosts : 0;

  if (error) {
    return (
      <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
            <div>
              <div className="text-xs font-bold text-red-900">
                Failed to load cost analysis data
              </div>
              <div className="text-xs text-red-700 mt-1">
                {error.message}
              </div>
            </div>
            <button
              onClick={handleRefresh}
              className="ml-auto px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-xs font-medium hover:bg-gray-50"
            >
              <FontAwesomeIcon icon={faRefresh} className="mr-1" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
      {/* Header with Status */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${loading ? 'bg-blue-400 animate-pulse' : 'bg-green-400'}`}></div>
            <div>
              <div className="text-base font-bold text-gray-900">
                Cost Analysis
              </div>
              <div className="text-xs text-gray-600">
                {data ?
                  `AWS: $${data.clouds.aws.totalSpend.toLocaleString()} • GCP: $${data.clouds.gcp.totalSpend.toLocaleString()} • GitHub: $${data.clouds.github.totalSpend.toLocaleString()}${includeCustomData ? ` • Custom: $${totalCustomCosts.toLocaleString()}` : ''}` :
                  'Loading...'
                }
                {lastUpdated && (
                  <span className="ml-2">• {lastUpdated.toLocaleTimeString()}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50"
            >
              <FontAwesomeIcon icon={faFilter} className="mr-1" />
              Filters
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faRefresh} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50">
              <FontAwesomeIcon icon={faDownload} className="mr-1" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-gray-900">Cost Filters</h3>
            <div className="flex space-x-2">
              <button
                onClick={handleClearFilters}
                className="px-2 py-1 text-xs bg-white border border-gray-300 text-gray-600 rounded hover:bg-gray-50 font-medium"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 font-medium"
              >
                Close
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Cloud Provider Filter */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Cloud Providers</label>
              <div className="space-y-1">
                {['aws', 'gcp', 'github'].map(cloud => (
                  <label key={cloud} className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={filters.cloud?.includes(cloud as any) || false}
                      onChange={(e) => {
                        const newFilters = { ...filters };
                        if (e.target.checked) {
                          newFilters.cloud = [...(newFilters.cloud || []), cloud as any];
                        } else {
                          newFilters.cloud = (newFilters.cloud || []).filter(c => c !== cloud);
                        }
                        handleApplyFilters(newFilters);
                      }}
                    />
                    {cloud.toUpperCase()}
                  </label>
                ))}
              </div>
            </div>
            
            {/* Cost Range Filter */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Cost Range</label>
              <div className="space-y-1">
                <input
                  type="number"
                  placeholder="Min cost"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  value={filters.costRange?.min || ''}
                  onChange={(e) => {
                    const newFilters = { ...filters };
                    newFilters.costRange = {
                      min: Number(e.target.value) || 0,
                      max: newFilters.costRange?.max || Infinity
                    };
                    handleApplyFilters(newFilters);
                  }}
                />
                <input
                  type="number"
                  placeholder="Max cost"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  value={filters.costRange?.max === Infinity ? '' : filters.costRange?.max || ''}
                  onChange={(e) => {
                    const newFilters = { ...filters };
                    newFilters.costRange = {
                      min: newFilters.costRange?.min || 0,
                      max: Number(e.target.value) || Infinity
                    };
                    handleApplyFilters(newFilters);
                  }}
                />
              </div>
            </div>
            
            {/* Date Range Filter */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Date Range</label>
              <div className="space-y-1">
                <input
                  type="date"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  value={filters.dateRange?.start || ''}
                  onChange={(e) => {
                    const newFilters = { ...filters };
                    newFilters.dateRange = {
                      start: e.target.value,
                      end: newFilters.dateRange?.end || ''
                    };
                    handleApplyFilters(newFilters);
                  }}
                />
                <input
                  type="date"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  value={filters.dateRange?.end || ''}
                  onChange={(e) => {
                    const newFilters = { ...filters };
                    newFilters.dateRange = {
                      start: newFilters.dateRange?.start || '',
                      end: e.target.value
                    };
                    handleApplyFilters(newFilters);
                  }}
                />
              </div>
            </div>
            
            {/* Custom Data Toggle */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Data Sources</label>
              <label className="flex items-center text-xs">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={includeCustomData}
                  onChange={(e) => setIncludeCustomData(e.target.checked)}
                />
                Include Custom Data
              </label>
              <div className="text-xs text-gray-500 mt-1">
                ${totalCustomCosts.toLocaleString()} from {customEntries.length} entries
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white border border-gray-300 rounded">
        <div className="flex space-x-1 p-1">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-2 py-1 text-xs font-medium rounded ${
              activeTab === 'summary'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => setActiveTab('visualization')}
            className={`px-2 py-1 text-xs font-medium rounded ${
              activeTab === 'visualization'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faChartBar} className="mr-1 text-xs" />
            Visualization
          </button>
          <button
            onClick={() => setActiveTab('custom-data')}
            className={`px-2 py-1 text-xs font-medium rounded ${
              activeTab === 'custom-data'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faDatabase} className="mr-1 text-xs" />
            Custom Data
          </button>
          <button
            onClick={() => setActiveTab('report')}
            className={`px-2 py-1 text-xs font-medium rounded ${
              activeTab === 'report'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faFileAlt} className="mr-1 text-xs" />
            Report
          </button>
          <button
            onClick={() => setActiveTab('bills')}
            className={`px-2 py-1 text-xs font-medium rounded ${
              activeTab === 'bills'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faFileInvoice} className="mr-1 text-xs" />
            Bills
          </button>
          <button
            onClick={() => setActiveTab('monthly-trends')}
            className={`px-2 py-1 text-xs font-medium rounded ${
              activeTab === 'monthly-trends'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faChartLine} className="mr-1 text-xs" />
            Monthly Trends
          </button>
          <button
            onClick={() => setActiveTab('reconciliation')}
            className={`px-2 py-1 text-xs font-medium rounded ${
              activeTab === 'reconciliation'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faBalanceScale} className="mr-1 text-xs" />
            Reconciliation
          </button>
          <button
            onClick={() => setActiveTab('unit-economics')}
            className={`px-2 py-1 text-xs font-medium rounded ${
              activeTab === 'unit-economics'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faChartLine} className="mr-1 text-xs" />
            Unit Economics
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-3">
        {activeTab === 'summary' && (
          <div className="space-y-3">
            {/* Cost Summary Section */}
            <CostSummary
              summary={data?.summary || null}
              loading={loading}
              customCosts={effectiveCustomCosts}
              customEntries={includeCustomData ? customEntries : []}
              csvImportResults={csvImportResults}
              costData={data || null}
            />

            {/* Cloud Breakdown Chart */}
            <CloudBreakdownChart data={data} loading={loading} />

            {/* Account Cost Table */}
            {data && (
              <div>
                <AccountCostTable data={data} loading={loading} maxItems={8} />
              </div>
            )}
          </div>
        )}

        {activeTab === 'visualization' && (
          <VisualizationTab 
            data={data}
            loading={loading}
            filters={filters}
            onFiltersChange={handleApplyFilters}
          />
        )}

        {activeTab === 'custom-data' && (
          <>
            {customError && (
              <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded">
                {customError}
              </div>
            )}
            <CustomDataTab
              entries={customEntries}
              onAddEntry={handleAddCustomEntry}
              onUpdateEntry={handleUpdateCustomEntry}
              onDeleteEntry={handleDeleteCustomEntry}
              onCSVImport={handleCSVImport}
              loading={customLoading}
            />
          </>
        )}

        {activeTab === 'report' && (
          <ReportTab
            data={data}
            loading={loading}
          />
        )}

        {activeTab === 'bills' && (
          <BillsTab
            data={data}
            loading={loading}
          />
        )}

        {activeTab === 'monthly-trends' && (
          <MonthlyTrendsTab
            data={data}
            loading={loading}
          />
        )}

        {activeTab === 'reconciliation' && (
          <ReconciliationTab
            data={data}
            loading={loading}
            customCosts={effectiveCustomCosts}
            customEntries={includeCustomData ? customEntries : []}
          />
        )}

        {activeTab === 'unit-economics' && (
          <UnitEconomicsTab
            data={data}
            loading={loading}
            customCosts={effectiveCustomCosts}
          />
        )}

      </div>
    </div>
  );
};

export default CostAnalysisTab;